<template>
  <div class="max-w-2xl mx-auto space-y-6">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">
        {{ $t('AI_ONBOARDING.PROFILE.TITLE') || 'Thông tin' }}
      </h2>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('AI_ONBOARDING.PROFILE.SUBTITLE') || 'Đặt tên và cá nhân hóa chatbot' }}
      </p>
    </div>

    <!-- Form -->
    <div class="space-y-4">
      <!-- Bot Name -->
      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          <lucide-icon name="bot" :size="16" class="inline mr-1" />
          {{ $t('AI_ONBOARDING.PROFILE.BOT_NAME') || 'Tên Chatbot' }} <span class="text-red-500">*</span>
        </label>
        <input
          v-model="formData.botName"
          type="text"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-hichat-500 text-sm"
          :placeholder="$t('AI_ONBOARDING.PROFILE.BOT_NAME_PLACEHOLDER') || 'Mai Bot, Trợ lý ảo Hoa...'"
          maxlength="50"
          @input="validateBotName"
        />
        <p v-if="errors.botName" class="text-xs text-red-600 mt-1">{{ errors.botName }}</p>
        <p v-else class="text-xs text-slate-500 mt-1">
          {{ $t('AI_ONBOARDING.PROFILE.BOT_NAME_HINT') || 'Tên này sẽ hiển thị khi chatbot trò chuyện' }}
        </p>
      </div>

      <!-- Business Category -->
      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          <lucide-icon name="briefcase" :size="16" class="inline mr-1" />
          {{ $t('AI_ONBOARDING.PROFILE.BUSINESS_CATEGORY') || 'Ngành nghề / Lĩnh vực' }} <span class="text-red-500">*</span>
        </label>
        <input
          v-model="formData.businessCategory"
          type="text"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-hichat-500 text-sm"
          :placeholder="$t('AI_ONBOARDING.PROFILE.BUSINESS_CATEGORY_PLACEHOLDER') || 'Thời trang, F&B, Bất động sản...'"
          maxlength="100"
        />
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {{ $t('AI_ONBOARDING.PROFILE.BUSINESS_CATEGORY_HINT') || 'Loại doanh nghiệp của bạn (ví dụ: Thời trang, F&B, Bất động sản)' }}
        </p>
      </div>

      <!-- Business Name -->
      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          <lucide-icon name="building" :size="16" class="inline mr-1" />
          {{ $t('AI_ONBOARDING.PROFILE.BUSINESS_NAME') || 'Tên doanh nghiệp' }}
        </label>
        <input
          v-model="formData.businessName"
          type="text"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-hichat-500 text-sm"
          :placeholder="$t('AI_ONBOARDING.PROFILE.BUSINESS_NAME_PLACEHOLDER') || 'Công ty TNHH ABC, Shop XYZ...'"
          maxlength="100"
        />
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {{ $t('AI_ONBOARDING.PROFILE.BUSINESS_NAME_HINT') || 'Tên cụ thể của doanh nghiệp (không bắt buộc)' }}
        </p>
      </div>

      <!-- Language -->
      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          <lucide-icon name="languages" :size="16" class="inline mr-1" />
          {{ $t('AI_ONBOARDING.PROFILE.LANGUAGE') || 'Ngôn ngữ chính' }} <span class="text-red-500">*</span>
        </label>
        <select
          v-model="formData.language"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-hichat-500 text-sm"
        >
          <option value="vi">🇻🇳 Tiếng Việt</option>
          <option value="en">🇺🇸 English</option>
          <option value="auto">{{ $t('AI_ONBOARDING.PROFILE.LANGUAGE_AUTO') || '🌐 Theo ngôn ngữ người dùng' }}</option>
        </select>
      </div>

      <!-- Addressing Style -->
      <div>
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          <lucide-icon name="message-circle" :size="16" class="inline mr-1" />
          {{ $t('AI_ONBOARDING.PROFILE.STYLE') || 'Cách xưng hô' }}
        </label>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label
            v-for="style in addressingStyles"
            :key="style.value"
            class="relative flex flex-col p-3 border-2 rounded-lg cursor-pointer transition-all"
            :class="formData.addressingStyle === style.value 
              ? 'border-grass-500 bg-grass-50 dark:bg-grass-900/20' 
              : 'border-slate-200 dark:border-slate-700 hover:border-grass-300'"
          >
            <input
              v-model="formData.addressingStyle"
              type="radio"
              :value="style.value"
              class="sr-only"
            />
            <div class="text-2xl text-center mb-1">{{ style.icon }}</div>
            <div class="text-center text-sm font-semibold text-slate-900 dark:text-white">{{ style.label }}</div>
            <div class="text-xs text-slate-600 dark:text-slate-400 text-center mt-1">{{ style.example }}</div>
          </label>
        </div>
      </div>

      <!-- Preview -->
      <div class="p-4 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/50 rounded-xl space-y-3">
        <h4 class="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <lucide-icon name="eye" :size="16" />
          {{ $t('AI_ONBOARDING.PROFILE.PREVIEW') || 'Xem trước' }}
        </h4>
        <div class="space-y-2">
          <div class="flex justify-end">
            <div class="px-3 py-2 bg-n-vars-right-bubble text-white rounded-xl rounded-br-none text-sm max-w-[80%]">
              Xin chào, cho tôi hỏi về sản phẩm?
            </div>
          </div>
          <div class="flex items-start gap-2">
            <div class="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shrink-0">
              <lucide-icon name="bot" :size="16" class="text-white" />
            </div>
            <div class="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl rounded-tl-none text-sm">
              <strong class="text-xs text-pink-600 dark:text-pink-400">{{ formData.botName || 'Bot' }}</strong>
              <p class="text-slate-700 dark:text-slate-300 mt-1">{{ previewMessage }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import LucideIcon from 'dashboard/components/LucideIcon.vue';

export default {
  name: 'AiBotProfileStep',
  
  components: {
    LucideIcon,
  },

  emits: ['next', 'back'],

  data() {
    return {
      formData: {
        botName: '',
        businessCategory: '',
        businessName: '',
        language: 'vi',
        addressingStyle: 'friendly',
      },
      errors: {},
    };
  },

  computed: {
    ...mapState('aiOnboarding', { profileState: state => state.botProfile }),

    addressingStyles() {
      return [
        {
          value: 'formal',
          label: this.$t('AI_ONBOARDING.PROFILE.STYLE_FORMAL_LABEL') || 'Trang trọng',
          icon: '👔',
          example: this.$t('AI_ONBOARDING.PROFILE.STYLE_FORMAL_EXAMPLE') || 'Kính chào Quý khách',
        },
        {
          value: 'friendly',
          label: this.$t('AI_ONBOARDING.PROFILE.STYLE_FRIENDLY_LABEL') || 'Thân thiện',
          icon: '😊',
          example: this.$t('AI_ONBOARDING.PROFILE.STYLE_FRIENDLY_EXAMPLE') || 'Chào bạn!',
        },
        {
          value: 'casual',
          label: this.$t('AI_ONBOARDING.PROFILE.STYLE_CASUAL_LABEL') || 'Thoải mái',
          icon: '🎉',
          example: this.$t('AI_ONBOARDING.PROFILE.STYLE_CASUAL_EXAMPLE') || 'Hiii!',
        },
      ];
    },

    previewMessage() {
      const { botName, businessCategory, businessName, addressingStyle } = this.formData;
      const greetings = {
        formal: this.$t('AI_ONBOARDING.PROFILE.GREETING_FORMAL') || 'Kính chào Quý khách',
        friendly: this.$t('AI_ONBOARDING.PROFILE.GREETING_FRIENDLY') || 'Chào bạn',
        casual: this.$t('AI_ONBOARDING.PROFILE.GREETING_CASUAL') || 'Hiii bạn',
      };
      const greeting = greetings[addressingStyle] || greetings.friendly;
      const botIntro = botName
        ? (this.$t('AI_ONBOARDING.PROFILE.INTRO_WITH_NAME', { name: botName }) || `Mình là ${botName}`)
        : (this.$t('AI_ONBOARDING.PROFILE.INTRO_GENERIC') || 'Mình là chatbot');
      
      let businessContext = '';
      if (businessName) {
        businessContext = this.$t('AI_ONBOARDING.PROFILE.INTRO_BUSINESS_NAME', { name: businessName }) || ` của ${businessName}`;
      } else if (businessCategory) {
        businessContext = this.$t('AI_ONBOARDING.PROFILE.INTRO_BUSINESS_CATEGORY', { category: businessCategory }) || ` chuyên về ${businessCategory}`;
      }
      
      const helpText = this.$t('AI_ONBOARDING.PROFILE.INTRO_HELP') || 'Mình có thể giúp gì cho bạn?';
      return `${greeting}! ${botIntro}${businessContext}. ${helpText}`;
    },

    isValid() {
      return this.formData.botName.trim().length >= 2 && this.formData.businessCategory.trim().length >= 2;
    },

    // Expose canProceed để container có thể check
    canProceed() {
      return this.isValid;
    },
  },

  mounted() {
    if (this.profileState && (this.profileState.botName || this.profileState.businessCategory || this.profileState.businessName)) {
      this.formData = { ...this.formData, ...this.profileState };
    }
  },

  watch: {
    formData: {
      deep: true,
      handler(newVal) {
        if (newVal.botName && newVal.businessCategory) {
          this.updateBotProfile(newVal);
        }
      },
    },
  },

  methods: {
    ...mapActions('aiOnboarding', ['updateBotProfile']),

    getCanProceed() {
      return this.canProceed;
    },

    validateBotName() {
      if (this.formData.botName.trim().length < 2) {
        this.errors.botName = this.$t('AI_ONBOARDING.PROFILE.NAME_ERROR') || 'Tên chatbot phải có ít nhất 2 ký tự';
      } else {
        delete this.errors.botName;
      }
    },

    async handleNext() {
      if (!this.isValid) {
        this.errors.botName = this.$t('AI_ONBOARDING.PROFILE.NAME_REQUIRED') || 'Vui lòng đặt tên cho chatbot';
        return;
      }

      await this.updateBotProfile(this.formData);
      this.$emit('next');
    },
  },
};
</script>
